/**
 *
 * Carousel
 *
 */

import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import ResizeObserver from 'resize-observer-polyfill';
import _noop from 'lodash/noop';
import _debounce from 'lodash/debounce';

import Button from '@mlb/react-patterns/components/Button/button.tsx';
import BREAKPOINTS from '@mlb/react-patterns/style/breakpoints.ts';
import { media } from '@mlb/react-patterns/style/helpers/media.ts';

import PopoverContent, { POPOVER_HORIZONTAL_BORDER } from 'bdvp-common/src/components/Card/PopoverContent';
import passiveArg from 'bdvp-common/src/utils/passiveArg';
import { mainCq } from 'bdvp-common/src/styles/helpers/main-cq';
import {
    useDidUpdate,
    useMainContainerQuery,
} from 'bdvp-common/src/hooks';

import { HORIZONTAL_PADDING } from './constants';

const CONTAINER_PADDING = '50px';
const CAROUSEL_ITEM_PADDING = 10;
const PARTIAL_VISIBILITY_GUTTER = {
    xs: 50,
    sm: 40,
    md: 30,
    lg: 0,
    xl: 0,
};
const LAYOUT_ITEMS = {
    xs: 1,
    sm: 2,
    md: 3,
    lg: 4,
    xl: 5,
};

const Wrapper = styled.div`
    position: relative;
    overflow: hidden;
    width: 100%;
    margin: -${CAROUSEL_ITEM_PADDING}px 0;
`;

export const ScrollContainer = styled.div`
    overflow-x: ${({ isScrollEnabled }) => isScrollEnabled ? 'auto' : 'hidden'};
    margin-bottom: -${CONTAINER_PADDING};
    width: 100%;
    padding: 0 ${HORIZONTAL_PADDING.xs - CAROUSEL_ITEM_PADDING}px ${CONTAINER_PADDING};
    white-space: nowrap;
    scroll-snap-type: x mandatory;
    scroll-padding: ${HORIZONTAL_PADDING.xs - CAROUSEL_ITEM_PADDING}px;
    overflow-anchor: none;

    ${media.mdmin`
        padding: 0 ${HORIZONTAL_PADDING.md - CAROUSEL_ITEM_PADDING}px ${CONTAINER_PADDING};
        scroll-padding: ${HORIZONTAL_PADDING.md - CAROUSEL_ITEM_PADDING}px;
    `}

    ${media.lgmin`
        padding: 0 ${HORIZONTAL_PADDING.lg - CAROUSEL_ITEM_PADDING}px ${CONTAINER_PADDING};
        scroll-padding: ${HORIZONTAL_PADDING.lg - CAROUSEL_ITEM_PADDING}px;
    `}

    ${PopoverContent} {
        ${media.xsmax`
            width: calc(100vw - ${PARTIAL_VISIBILITY_GUTTER.xs + (HORIZONTAL_PADDING.xs * 2)}px - ${POPOVER_HORIZONTAL_BORDER * 2}px);
        `}
    }
`;

const Item = styled.div`
    display: inline-block;
    width: calc(100% - ${PARTIAL_VISIBILITY_GUTTER.xs}px);
    padding: ${CAROUSEL_ITEM_PADDING}px;
    vertical-align: top;
    white-space: normal;
    scroll-snap-align: start;

    ${mainCq.sm`
        width: calc(${100 / LAYOUT_ITEMS.sm}% - ${PARTIAL_VISIBILITY_GUTTER.sm}px);
    `}

    ${mainCq.md`
        width: calc(${100 / LAYOUT_ITEMS.md}% - ${PARTIAL_VISIBILITY_GUTTER.md}px);
    `}

    ${mainCq.lg`
        width: ${100 / LAYOUT_ITEMS.lg}%;
    `}

    ${mainCq.xl`
        width: ${100 / LAYOUT_ITEMS.xl}%;
    `}

    /* Firefox ignores padding when using overflow scroll */
    @supports (-moz-appearance:none) {
        &:last-of-type {
            margin-right: ${HORIZONTAL_PADDING.xs - CAROUSEL_ITEM_PADDING}px;

            ${mainCq.md`
                margin-right: ${HORIZONTAL_PADDING.md - CAROUSEL_ITEM_PADDING}px;
            `}

            ${mainCq.lg`
                margin-right: ${HORIZONTAL_PADDING.lg - CAROUSEL_ITEM_PADDING}px;
            `}
        }
    }
`;

const NavButton = styled(Button).attrs({
    shape: 'hollow-borderless',
    styleName: 'default',
    iconSize: 23,
    hasHover: false,
})`
    position: absolute;
    z-index: 1;
    top: 0;
    border-radius: 0;
    width: 32px;
    height: 100%;
    padding: 0;
    background: rgba(0, 0, 0, 0.8);
`;

const NextButton = styled(NavButton).attrs({
    iconName: 'carousel-arrow-right',
})`
    right: 0;
`;

const PrevButton = styled(NavButton).attrs({
    iconName: 'carousel-arrow-left',
})`
    left: 0;
`;

let isScrolling;

export const imgSizes = [
    `(min-width: ${BREAKPOINTS.xl}) calc(((100vw - ${(HORIZONTAL_PADDING.lg - CAROUSEL_ITEM_PADDING) * 2}px) / ${LAYOUT_ITEMS.xl}) - ${CAROUSEL_ITEM_PADDING * 2}px),`,
    `(min-width: ${BREAKPOINTS.lg}) calc(((100vw - ${(HORIZONTAL_PADDING.lg - CAROUSEL_ITEM_PADDING) * 2}px) / ${LAYOUT_ITEMS.lg}) - ${CAROUSEL_ITEM_PADDING * 2}px),`,
    `(min-width: ${BREAKPOINTS.md}) calc(((100vw - ${((HORIZONTAL_PADDING.md - CAROUSEL_ITEM_PADDING) * 2) + PARTIAL_VISIBILITY_GUTTER.md}px) / ${LAYOUT_ITEMS.md}) - ${CAROUSEL_ITEM_PADDING * 2}px),`,
    `(min-width: ${BREAKPOINTS.sm}) calc(((100vw - ${((HORIZONTAL_PADDING.xs - CAROUSEL_ITEM_PADDING) * 2) + PARTIAL_VISIBILITY_GUTTER.sm}px) / ${LAYOUT_ITEMS.sm}) - ${CAROUSEL_ITEM_PADDING * 2}px),`,
    `calc(100vw - ${((HORIZONTAL_PADDING.xs - CAROUSEL_ITEM_PADDING) * 2) + PARTIAL_VISIBILITY_GUTTER.xs + (CAROUSEL_ITEM_PADDING * 2)}px)`,
].join('');

export const ANIMATION_DURATION_MS = 150;

const Carousel = (props) => {

    const {
        hasMore,
        isLoading,
        list,
        isLoadingMore,
        fetchMoreData,
        renderItem,
        onAfterChange,
    } = props;

    const scrollContainerRef = useRef(null);
    const observerRef = useRef(null);
    const loaderRef = useRef(null);
    const [
        scroll,
        setScroll,
    ] = useState(0);
    const [
        scrollWidth,
        setScrollWidth,
    ] = useState(0);
    const [
        containerWidth,
        setContainerWidth,
    ] = useState(0);
    const [
        hasMounted,
        setHasMounted,
    ] = useState(false);
    const touchingRef = useRef(false);
    const startingScrollPosition = useRef(scroll);

    const resizeCallback = _debounce(
        ([entry]) => {

            setScrollWidth(entry.target.scrollWidth);
            setContainerWidth(entry.target.clientWidth);

        },
        100
    );

    if (typeof window !== 'undefined' && !observerRef.current) {

        observerRef.current = new ResizeObserver(resizeCallback);

    }

    const onScroll = useCallback((event) => {

        // Clear our timeout throughout the scroll
        window.clearTimeout(isScrolling);

        // Set a timeout to run after scrolling/snapping ends
        isScrolling = setTimeout(function() {

            if (
                !touchingRef.current
                && startingScrollPosition.current !== scrollContainerRef.current.scrollLeft
            ) {

                onAfterChange(scrollContainerRef.current.scrollLeft < startingScrollPosition.current);

            }

        }, ANIMATION_DURATION_MS);

        setScroll(scrollContainerRef.current.scrollLeft);

    }, [
        scrollContainerRef,
        setScroll,
        touchingRef,
        onAfterChange,
    ]);

    const scrollToX = useCallback((scrollTarget) => {

        if (!scrollContainerRef.current) {

            return null;

        }

        if ('scrollBehavior' in document.documentElement.style) {

            return scrollContainerRef.current.scrollTo({
                left: scrollTarget,
                behavior: 'smooth',
            });

        }

        let currentTime = 0;

        // animation duration in seconds
        const time = ANIMATION_DURATION_MS/1000;

        // add animation loop
        const tick = () => {

            currentTime += 1 / 60;

            const pos = currentTime / time;
            // easeInOutSine - easing equations from https://github.com/danro/easing-js/blob/master/easing.js
            const easing = -0.5 * (Math.cos(Math.PI * pos) - 1);

            if (pos < 1) {

                window.requestAnimationFrame(tick);
                scrollContainerRef.current.scrollLeft = scrollContainerRef.current.scrollLeft + (scrollTarget - scrollContainerRef.current.scrollLeft) * easing;

            } else {

                scrollContainerRef.current.scrollLeft = scrollTarget;

            }

        };

        // call it once to get started
        tick();

    }, [scrollContainerRef]);

    const scrollToPadding = useMainContainerQuery(
        [
            'LG',
            'MD',
            'XS',
        ],
        [
            (HORIZONTAL_PADDING.lg - CAROUSEL_ITEM_PADDING) * 2,
            (HORIZONTAL_PADDING.md - CAROUSEL_ITEM_PADDING) * 2 + PARTIAL_VISIBILITY_GUTTER.md,
            (HORIZONTAL_PADDING.xs - CAROUSEL_ITEM_PADDING) * 2 + PARTIAL_VISIBILITY_GUTTER.sm,
        ],
        (HORIZONTAL_PADDING.xs - CAROUSEL_ITEM_PADDING) * 2 + PARTIAL_VISIBILITY_GUTTER.xs
    );

    const scrollToNext = useCallback(() => {

        const scrollTo = scroll + containerWidth - scrollToPadding;
        const maxScroll = scrollWidth - containerWidth;

        startingScrollPosition.current = scroll;
        scrollToX(scrollTo > maxScroll ? maxScroll : scrollTo);

    }, [
        scroll,
        scrollWidth,
        containerWidth,
        scrollToX,
        startingScrollPosition,
        scrollToPadding,
    ]);

    const scrollToPrev = useCallback(() => {

        const scrollTo = scroll - containerWidth + scrollToPadding;

        startingScrollPosition.current = scroll;
        scrollToX(scrollTo < 0 ? 0 : scrollTo);

    }, [
        scroll,
        containerWidth,
        scrollToX,
        startingScrollPosition,
        scrollToPadding,
    ]);

    const nextBtnHandlers = useMemo(() => ({
        onClick: scrollToNext,
    }), [scrollToNext]);

    const prevBtnHandlers = useMemo(() => ({
        onClick: scrollToPrev,
    }), [scrollToPrev]);

    const loadMore = useCallback((entries) => {

        const target = entries[0];

        if (
            target.isIntersecting
            && hasMore
            && !isLoadingMore
        ) {

            fetchMoreData();

        }

    }, [
        fetchMoreData,
        hasMore,
        isLoadingMore,
    ]);

    useEffect(() => {

        setHasMounted(true);

    }, [onScroll]);

    useEffect(() => {

        const container = scrollContainerRef.current;
        container.addEventListener('scroll', onScroll, passiveArg);

        return () => container.removeEventListener('scroll', onScroll);

    }, [onScroll]);

    useEffect(() => {

        const { current: observer } = observerRef;

        observer.observe(scrollContainerRef.current);

        return () => {

            observer.disconnect();

        };

    }, [
        observerRef,
        scrollContainerRef,
    ]);

    useEffect(() => {

        const options = {
            root: scrollContainerRef.current,
            rootMargin: '100%',
            threshold: 0,
        };

        const intersectionObserver = new IntersectionObserver(loadMore, options);

        if (loaderRef && loaderRef.current) {

            intersectionObserver.observe(loaderRef.current);

        }

        return () => intersectionObserver.disconnect();

    }, [
        scrollContainerRef,
        loaderRef,
        loadMore,
    ]);

    useDidUpdate(() => {

        setScrollWidth(scrollContainerRef.current.scrollWidth);

    }, [
        list,
        setScrollWidth,
    ]);

    const isTouchDevice = hasMounted && window.matchMedia('(hover: none)').matches;

    return (
        <Wrapper>
            {!isTouchDevice && scroll > 0 && (
                <PrevButton handlers={prevBtnHandlers} />
            )}
            <ScrollContainer
                ref={scrollContainerRef}
                isScrollEnabled={isTouchDevice}
                onTouchStart={() => {

                    touchingRef.current = true;
                    startingScrollPosition.current = scroll;

                }}
                onTouchEnd={() => touchingRef.current = false}
            >
                {isLoading
                    ? Array(LAYOUT_ITEMS.xl)
                        .fill({}, 0, LAYOUT_ITEMS.xl)
                        .map((item, index) => (
                            <Item key={`skeleton-${index}`}>
                                {renderItem(item, true, index)}
                            </Item>
                        ))
                    : list.map((item, index) => (
                        <Item key={`item-${index}`}>
                            {renderItem(item, false, index)}
                        </Item>
                    ))
                }
                {hasMore && (
                    <Item key={'loader'} ref={loaderRef}>
                        {renderItem({}, true)}
                    </Item>
                )}
            </ScrollContainer>
            {!isTouchDevice && (scroll + containerWidth) < scrollWidth && (
                <NextButton handlers={nextBtnHandlers} />
            )}
        </Wrapper>
    );

};

Carousel.propTypes = {
    isLoading: PropTypes.bool.isRequired,
    isLoadingMore: PropTypes.bool,
    list: PropTypes.array,
    fetchMoreData: PropTypes.func,
    hasMore: PropTypes.bool,
    renderItem: PropTypes.func,
    onAfterChange: PropTypes.func,
};

Carousel.defaultProps = {
    isLoadingMore: false,
    list: [],
    hasMore: false,
    onAfterChange: _noop,
    fetchMoreData: _noop,
    renderItem: _noop,
};

export default Carousel;
